import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getOccupancy, getTodayActivity, updateReservation, checkConflict, getReservations, getRetreats, fmt, fmtDate, fmtDateLong } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ReservationDrawer from "@/components/ReservationDrawer";
import AddReservationDialog from "@/components/AddReservationDialog";

const STATUS_BADGE = {
  booked: "bg-slate-100 text-slate-700 border-slate-200",
  checked_in: "bg-blue-100 text-blue-700 border-blue-200",
  checked_out: "bg-slate-200 text-slate-600 border-slate-300",
};

const BAR_COLORS = {
  paid:    "bg-emerald-500 hover:bg-emerald-600 border-emerald-700",
  partial: "bg-amber-500 hover:bg-amber-600 border-amber-700",
  unpaid:  "bg-red-500 hover:bg-red-600 border-red-700",
};

// Retreat lane colors — distinct from reservation payment colors so the two
// lanes don't get visually confused. Planned = yellow, Confirmed = green,
// In progress = blue, Completed = slate.
const RETREAT_BAR_COLORS = {
  planned:     "bg-yellow-300 hover:bg-yellow-400 text-yellow-950 border-yellow-500",
  confirmed:   "bg-emerald-400 hover:bg-emerald-500 text-emerald-950 border-emerald-600",
  in_progress: "bg-blue-400 hover:bg-blue-500 text-blue-950 border-blue-600",
  completed:   "bg-slate-300 hover:bg-slate-400 text-slate-800 border-slate-500",
};
const RETREAT_STATUS_LABEL = {
  planned: "Planned",
  confirmed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
};

const WEEK_CELL_W = 56;
const MONTH_CELL_W = 32;
const TRACK_H = 30; // bar height
const TRACK_GAP = 2;

// Timezone-safe helpers: always work in LOCAL time so "2026-03-01" renders as March 1
// in every timezone (plain .toISOString() shifts by the UTC offset, which was
// making March appear twice in browsers east of UTC).
const fmtLocalIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const parseIsoLocal = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Greedy track packing per unit: returns list of tracks, each track is non-overlapping reservations. */
function packTracks(reservations) {
  const sorted = [...reservations].sort((a, b) => a.check_in.localeCompare(b.check_in));
  const tracks = [];
  for (const r of sorted) {
    let placed = false;
    for (const track of tracks) {
      const last = track[track.length - 1];
      // No overlap if previous check_out is on/before this check_in
      if (last.check_out <= r.check_in) {
        track.push(r);
        placed = true;
        break;
      }
    }
    if (!placed) tracks.push([r]);
  }
  return tracks;
}

export default function Dashboard() {
  const [occ, setOcc] = useState({ dates: [], units: [], reservations: [] });
  const occRef = useRef(occ);
  occRef.current = occ;
  const [today, setToday] = useState({ check_ins: [], check_outs: [], date: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [viewMode, setViewMode] = useState("week"); // "week" | "month"
  const [startOffset, setStartOffset] = useState(0);   // days (week mode)
  const [monthOffset, setMonthOffset] = useState(0);   // months (month mode)
  // Tracks the most recent reservation in the DB so we can offer a "jump to" hint
  // when the visible window is empty.
  const [latestRes, setLatestRes] = useState(null);
  const [allRetreats, setAllRetreats] = useState([]);

  const CELL_W = viewMode === "month" ? MONTH_CELL_W : WEEK_CELL_W;

  // Drag state: { r, edge: 'left'|'right'|'move', startX, daysDelta, previewCheckIn, previewCheckOut, moved }
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  dragRef.current = drag;
  // Set to true on mouseup right after a real drag, to suppress the subsequent click
  const justDraggedRef = useRef(false);

  const load = async () => {
    let startIso; let days;
    if (viewMode === "month") {
      const base = new Date();
      const monthStart = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
      const monthEnd = new Date(base.getFullYear(), base.getMonth() + monthOffset + 1, 0);
      startIso = fmtLocalIso(monthStart);
      days = monthEnd.getDate();
    } else {
      const sd = new Date();
      sd.setDate(sd.getDate() + startOffset);
      startIso = fmtLocalIso(sd);
      days = 14;
    }
    const [o, t] = await Promise.all([
      getOccupancy(days, startIso),
      getTodayActivity(),
    ]);
    setOcc(o);
    setToday(t);
    // Refetch retreats alongside so the timeline reflects additions/edits
    // after navigating away and back.
    getRetreats().then(setAllRetreats).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startOffset, monthOffset, viewMode]);

  // Fetch the single most-recent reservation once at mount so we can show a
  // helpful "jump to {month}" notice if the user's default view is empty.
  useEffect(() => {
    getReservations({ page_size: 1, page: 1 })
      .then(d => {
        const items = (d && (d.items || d)) || [];
        if (items.length) setLatestRes(items[0]);
      })
      .catch(() => {});
    getRetreats().then(setAllRetreats).catch(() => {});
  }, []);

  const jumpToLatest = () => {
    if (!latestRes?.check_in) return;
    const today = new Date();
    const target = parseIsoLocal(latestRes.check_in);
    const monthsBack =
      (target.getFullYear() - today.getFullYear()) * 12 +
      (target.getMonth() - today.getMonth());
    setViewMode("month");
    setMonthOffset(monthsBack);
  };

  // True when the timeline shows nothing AND there is recent data we can jump to.
  // We count reservations whose date range intersects the current visible window
  // because the backend returns the full list, not just visible ones.
  const visibleReservationCount = useMemo(() => {
    if (!occ.dates.length || !occ.reservations) return 0;
    const first = parseIsoLocal(occ.dates[0]).getTime();
    const last = parseIsoLocal(occ.dates[occ.dates.length - 1]).getTime();
    return occ.reservations.filter(r => {
      const ci = parseIsoLocal(r.check_in).getTime();
      const co = parseIsoLocal(r.check_out).getTime();
      return ci <= last && co > first;
    }).length;
  }, [occ.dates, occ.reservations]);

  const showEmptyHint =
    visibleReservationCount === 0 &&
    !!latestRes &&
    !(viewMode === "month" && monthOffset !== 0);

  // Drag handlers — track mouse to preview & PATCH on release
  useEffect(() => {
    if (!drag) return;
    const addDaysIso = (iso, days) => {
      const d = parseIsoLocal(iso);
      d.setDate(d.getDate() + days);
      return fmtLocalIso(d);
    };
    const MOVE_THRESHOLD = 3; // px before considered a drag (not a click)
    const computePreview = (edge, r, daysDelta) => {
      if (edge === "right") {
        let co = addDaysIso(r.check_out, daysDelta);
        if (co <= r.check_in) co = addDaysIso(r.check_in, 1);
        return { previewCheckIn: r.check_in, previewCheckOut: co };
      }
      if (edge === "left") {
        let ci = addDaysIso(r.check_in, daysDelta);
        if (ci >= r.check_out) ci = addDaysIso(r.check_out, -1);
        return { previewCheckIn: ci, previewCheckOut: r.check_out };
      }
      // move: shift both by same delta
      return {
        previewCheckIn: addDaysIso(r.check_in, daysDelta),
        previewCheckOut: addDaysIso(r.check_out, daysDelta),
      };
    };
    // Walk up from an element to find the row's data-unit-id attribute.
    const unitAtPoint = (x, y) => {
      const el = document.elementFromPoint(x, y);
      if (!el) return null;
      const row = el.closest?.("[data-unit-id]");
      return row ? row.getAttribute("data-unit-id") : null;
    };
    const onMove = (e) => {
      const cur = dragRef.current;
      if (!cur) return;
      const dx = e.clientX - cur.startX;
      const moved = cur.moved || Math.abs(dx) > MOVE_THRESHOLD;
      const daysDelta = Math.round(dx / CELL_W);
      const { previewCheckIn, previewCheckOut } = computePreview(cur.edge, cur.r, daysDelta);
      // Only "move" drags can reassign rooms vertically.
      let previewUnitId = cur.r.unit_id;
      if (cur.edge === "move") {
        const hit = unitAtPoint(e.clientX, e.clientY);
        if (hit) previewUnitId = hit;
      }
      if (
        moved !== cur.moved ||
        daysDelta !== cur.daysDelta ||
        previewCheckIn !== cur.previewCheckIn ||
        previewCheckOut !== cur.previewCheckOut ||
        previewUnitId !== cur.previewUnitId
      ) {
        setDrag({ ...cur, moved, daysDelta, previewCheckIn, previewCheckOut, previewUnitId });
      }
    };
    const onUp = async () => {
      const cur = dragRef.current;
      setDrag(null);
      if (!cur || !cur.moved) return;
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 100);
      const patch = {};
      if (cur.previewCheckIn !== cur.r.check_in) patch.check_in = cur.previewCheckIn;
      if (cur.previewCheckOut !== cur.r.check_out) patch.check_out = cur.previewCheckOut;
      if (cur.edge === "move" && cur.previewUnitId && cur.previewUnitId !== cur.r.unit_id) {
        patch.unit_id = cur.previewUnitId;
      }
      if (!Object.keys(patch).length) return;
      // Pre-check availability before committing a room reassignment.
      if (patch.unit_id) {
        try {
          const res = await checkConflict({
            unit_id: patch.unit_id,
            check_in: patch.check_in || cur.r.check_in,
            check_out: patch.check_out || cur.r.check_out,
            exclude_id: cur.r.id,
          });
          if (res?.conflict) {
            const names = (res.reservations || [])
              .map(x => x.guest_name).filter(Boolean).slice(0, 2).join(", ");
            toast.error(`Cannot move — target bed is booked${names ? ` by ${names}` : ""}.`);
            return;
          }
        } catch {
          toast.error("Could not verify availability");
          return;
        }
      }
      try {
        await updateReservation(cur.r.id, patch);
        const unitName = patch.unit_id
          ? (occRef.current.units || []).find(u => u.id === patch.unit_id)?.display_name
          : null;
        const label =
          patch.unit_id
            ? `moved to ${unitName || "new unit"}`
            : cur.edge === "right" ? `checks out ${cur.previewCheckOut}`
            : cur.edge === "left"  ? `checks in ${cur.previewCheckIn}`
            : `${cur.previewCheckIn} → ${cur.previewCheckOut}`;
        toast.success(`${cur.r.guest_name} ${label.startsWith("moved") ? label : `now ${label}`}`);
        load();
      } catch {
        toast.error("Could not update reservation");
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag?.r?.id]); // eslint-disable-line

  const openDrawer = (id) => { setSelectedId(id); setDrawerOpen(true); };

  // Reservations grouped by unit_id
  const resByUnit = useMemo(() => {
    const m = {};
    for (const r of occ.reservations || []) {
      (m[r.unit_id] = m[r.unit_id] || []).push(r);
    }
    return m;
  }, [occ.reservations]);

  // Pre-pack tracks per unit
  const tracksByUnit = useMemo(() => {
    const out = {};
    for (const uid of Object.keys(resByUnit)) {
      out[uid] = packTracks(resByUnit[uid]);
    }
    return out;
  }, [resByUnit]);

  // Per-date check-in / check-out counts within visible range
  const perDateStats = useMemo(() => {
    const stats = {};
    for (const d of occ.dates) stats[d] = { in: 0, out: 0 };
    for (const r of occ.reservations || []) {
      if (stats[r.check_in] !== undefined) stats[r.check_in].in += 1;
      if (stats[r.check_out] !== undefined) stats[r.check_out].out += 1;
    }
    return stats;
  }, [occ.dates, occ.reservations]);

  // Group units by their group name while preserving order
  const groupedUnits = useMemo(() => {
    const g = {};
    for (const u of occ.units || []) {
      (g[u.group] = g[u.group] || []).push(u);
    }
    return g;
  }, [occ.units]);

  const todayISO = fmtLocalIso(new Date());
  const firstDateMs = occ.dates.length ? parseIsoLocal(occ.dates[0]).getTime() : 0;
  const lastDateMs = occ.dates.length ? parseIsoLocal(occ.dates[occ.dates.length - 1]).getTime() : 0;

  /** Compute bar placement for one reservation within visible window.
   *  Returns { leftPx, widthPx, clipLeft, clipRight } or null if fully outside. */
  const computeBar = (r) => {
    if (!occ.dates.length) return null;
    const ci = parseIsoLocal(r.check_in).getTime();
    const co = parseIsoLocal(r.check_out).getTime();
    // Fully outside visible range?
    if (co < firstDateMs) return null;
    if (ci > lastDateMs + 86400000) return null;

    const dayMs = 86400000;
    const ciIdx = (ci - firstDateMs) / dayMs; // may be negative
    const coIdx = (co - firstDateMs) / dayMs; // may exceed dates.length

    // Half-day offsets: check-in = afternoon (center of cell), check-out = morning (center of cell)
    let leftIdx = ciIdx + 0.5;
    let rightIdx = coIdx + 0.5;

    const clipLeft = leftIdx < 0;
    const clipRight = rightIdx > occ.dates.length;

    if (clipLeft) leftIdx = 0;
    if (clipRight) rightIdx = occ.dates.length;

    return {
      leftPx: leftIdx * CELL_W,
      widthPx: (rightIdx - leftIdx) * CELL_W,
      clipLeft,
      clipRight,
    };
  };

  /** Retreats use full-day bars (no half-day offset like reservations).
   *  end_date is inclusive, so the bar ends at start-of(end_date+1). */
  const computeRetreatBar = (startIso, endIso) => {
    if (!occ.dates.length) return null;
    const dayMs = 86400000;
    const s = parseIsoLocal(startIso).getTime();
    const eExclusive = parseIsoLocal(endIso).getTime() + dayMs;
    if (eExclusive <= firstDateMs) return null;
    if (s > lastDateMs + dayMs) return null;
    let leftIdx = (s - firstDateMs) / dayMs;
    let rightIdx = (eExclusive - firstDateMs) / dayMs;
    const clipLeft = leftIdx < 0;
    const clipRight = rightIdx > occ.dates.length;
    if (clipLeft) leftIdx = 0;
    if (clipRight) rightIdx = occ.dates.length;
    return {
      leftPx: leftIdx * CELL_W,
      widthPx: (rightIdx - leftIdx) * CELL_W,
      clipLeft,
      clipRight,
    };
  };

  /** Retreats overlapping the visible window (excludes cancelled). */
  const visibleRetreats = useMemo(() => {
    if (!occ.dates.length) return [];
    const first = occ.dates[0];
    const last = occ.dates[occ.dates.length - 1];
    const lastNext = (() => {
      const d = parseIsoLocal(last);
      d.setDate(d.getDate() + 1);
      return fmtLocalIso(d);
    })();
    return (allRetreats || []).filter(
      r => r.status !== "cancelled" && r.start_date < lastNext && r.end_date >= first,
    );
  }, [allRetreats, occ.dates]);

  /** Pack retreats into non-overlapping tracks for vertical stacking.
   *  We treat end_date as inclusive (a retreat from 3→5 blocks 3, 4, 5). */
  const retreatTracks = useMemo(() => {
    const sorted = [...visibleRetreats].sort((a, b) => a.start_date.localeCompare(b.start_date));
    const tracks = [];
    for (const r of sorted) {
      let placed = false;
      for (const track of tracks) {
        const last = track[track.length - 1];
        if (last.end_date < r.start_date) {
          track.push(r);
          placed = true;
          break;
        }
      }
      if (!placed) tracks.push([r]);
    }
    return tracks;
  }, [visibleRetreats]);

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Occupancy timeline · click any bar to open a reservation</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="dashboard-new-reservation-btn">
          + New Reservation
        </Button>
      </div>

      {/* Today */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 shadow-none border-slate-200" data-testid="today-checkins-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-blue-50 flex items-center justify-center">
                <LogIn size={16} className="text-blue-600" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading font-semibold text-slate-900">Today's Check-ins</h3>
            </div>
            <Badge variant="outline" className="bg-white font-heading font-bold" data-testid="today-checkins-count">{today.check_ins?.length || 0}</Badge>
          </div>
          {today.check_ins?.length ? (
            <div className="space-y-1.5">
              {today.check_ins.map(r => (
                <button
                  key={r.id}
                  onClick={() => openDrawer(r.id)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-left"
                  data-testid={`checkin-${r.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{r.guest_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.unit?.display_name}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_BADGE[r.status]}>{r.status.replace("_", " ")}</Badge>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-4 text-center">No check-ins today</div>
          )}
        </Card>

        <Card className="p-5 shadow-none border-slate-200" data-testid="today-checkouts-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-amber-50 flex items-center justify-center">
                <LogOut size={16} className="text-amber-600" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading font-semibold text-slate-900">Today's Check-outs</h3>
            </div>
            <Badge variant="outline" className="bg-white font-heading font-bold" data-testid="today-checkouts-count">{today.check_outs?.length || 0}</Badge>
          </div>
          {today.check_outs?.length ? (
            <div className="space-y-1.5">
              {today.check_outs.map(r => (
                <button
                  key={r.id}
                  onClick={() => openDrawer(r.id)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-colors text-left"
                  data-testid={`checkout-${r.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{r.guest_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.unit?.display_name}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_BADGE[r.status]}>{r.status.replace("_", " ")}</Badge>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-4 text-center">No check-outs today</div>
          )}
        </Card>
      </div>

      {/* Occupancy Timeline */}
      <Card className="shadow-none border-slate-200 overflow-visible" data-testid="occupancy-calendar">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-wrap gap-3">
          <div>
            <h3 className="font-heading font-semibold text-slate-900">Occupancy Timeline</h3>
            <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
              <div className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-emerald-500 inline-block"></span><span className="text-slate-600">Fully paid</span></div>
              <div className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-amber-500 inline-block"></span><span className="text-slate-600">Partial</span></div>
              <div className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-red-500 inline-block"></span><span className="text-slate-600">Unpaid</span></div>
              {visibleRetreats.length > 0 && (
                <>
                  <span className="h-3 w-px bg-slate-300 mx-1" />
                  <div className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-yellow-300 inline-block border border-yellow-500"></span><span className="text-slate-600">Retreat · planned</span></div>
                  <div className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-emerald-400 inline-block border border-emerald-600"></span><span className="text-slate-600">Retreat · confirmed</span></div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* View-mode toggle */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="cal-view-toggle">
              <button
                onClick={() => setViewMode("week")}
                className={`px-3 h-8 font-medium transition-colors ${viewMode === "week" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid="cal-view-week"
              >Week</button>
              <button
                onClick={() => setViewMode("month")}
                className={`px-3 h-8 font-medium transition-colors border-l border-slate-200 ${viewMode === "month" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid="cal-view-month"
              >Month</button>
            </div>
            {/* Range label (month only) */}
            {viewMode === "month" && occ.dates.length > 0 && (
              <span className="text-xs font-medium text-slate-600" data-testid="cal-month-label">
                {parseIsoLocal(occ.dates[0]).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
              </span>
            )}
            {/* Navigation */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => viewMode === "month" ? setMonthOffset(o => o - 1) : setStartOffset(o => o - 7)}
                data-testid="cal-prev"
              ><ChevronLeft size={14} /></Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => viewMode === "month" ? setMonthOffset(0) : setStartOffset(0)}
                data-testid="cal-today"
              >Today</Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => viewMode === "month" ? setMonthOffset(o => o + 1) : setStartOffset(o => o + 7)}
                data-testid="cal-next"
              ><ChevronRight size={14} /></Button>
            </div>
          </div>
        </div>

        {showEmptyHint && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap" data-testid="empty-window-hint">
            <div className="text-sm text-amber-900">
              <span className="font-semibold">No reservations in this window.</span>
              {" "}Most recent activity:{" "}
              <span className="font-medium">{latestRes.guest_name}</span>
              {" · "}{fmtDate(latestRes.check_in)} → {fmtDate(latestRes.check_out)}.
              <span className="text-amber-700/80"> Your data is safe — try jumping back.</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={jumpToLatest}
              className="h-8 border-amber-300 text-amber-900 bg-white"
              data-testid="empty-window-jump-btn"
            >
              Jump to {parseIsoLocal(latestRes.check_in).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
            </Button>
          </div>
        )}

        {/* Scroll container — both axes scroll inside this box, which makes
            position:sticky reliable for the dates header and the unit-name
            column. Height is capped to a fraction of viewport so the header
            never disappears off the page. */}
        <div
          style={{
            overflow: "auto",
            maxHeight: "calc(100vh - 220px)",
            scrollbarGutter: "stable",
          }}
          data-testid="cal-scroll-container"
        >
          <div style={{ minWidth: 144 + occ.dates.length * CELL_W }}>
            {/* Sticky header row — pinned to the top of THIS scroll container. */}
            <div className="flex border-b-2 border-slate-300 bg-white sticky top-0 z-20 shadow-[0_1px_0_rgba(15,23,42,0.04)]" data-testid="cal-header-row">
              <div className="w-[144px] shrink-0 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-600 border-r border-slate-200 bg-white sticky left-0 z-[21]">
                Unit
              </div>
              {occ.dates.map(d => {
                const dt = parseIsoLocal(d);
                const isToday = d === todayISO;
                const weekend = dt.getDay() === 0 || dt.getDay() === 6;
                const st = perDateStats[d] || { in: 0, out: 0 };
                return (
                  <div
                    key={d}
                    style={{ width: CELL_W }}
                    className={`shrink-0 px-1 py-1.5 text-center text-[10px] border-r border-slate-200 ${isToday ? "bg-blue-100 text-blue-700" : weekend ? "bg-slate-50 text-slate-500" : "text-slate-600"}`}
                  >
                    <div className="uppercase tracking-wider font-medium">{dt.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2)}</div>
                    <div className={`text-sm font-bold mt-0.5 ${isToday ? "text-blue-700" : "text-slate-900"}`}>{dt.getDate()}</div>
                    <div className="flex items-center justify-center gap-0.5 mt-0.5 text-[9px]" data-testid={`day-stats-${d}`}>
                      {st.in > 0 && <span className="inline-flex items-center gap-0.5 px-1 rounded bg-blue-100 text-blue-700 font-semibold"><LogIn size={8} strokeWidth={2.5} />{st.in}</span>}
                      {st.out > 0 && <span className="inline-flex items-center gap-0.5 px-1 rounded bg-amber-100 text-amber-700 font-semibold"><LogOut size={8} strokeWidth={2.5} />{st.out}</span>}
                      {st.in === 0 && st.out === 0 && <span>&nbsp;</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Retreats lane — shows planned/confirmed/in-progress retreats as
                horizontal bars above the room rows. Independent of room
                assignment because guests may not be assigned yet. */}
            {retreatTracks.length > 0 && (() => {
              const laneHeight = retreatTracks.length * (TRACK_H + TRACK_GAP) + TRACK_GAP;
              return (
                <div className="flex border-b border-slate-200 bg-yellow-50/30" data-testid="retreat-lane">
                  <div className="w-[144px] shrink-0 px-3 py-2 text-sm text-slate-700 bg-yellow-50/60 border-r border-slate-200 sticky left-0 z-[5] flex items-center gap-2">
                    <Sparkles size={14} className="text-amber-500" strokeWidth={2} />
                    <span className="font-medium">Retreats</span>
                  </div>
                  <div
                    className="relative flex-1"
                    style={{ height: laneHeight, width: occ.dates.length * CELL_W }}
                  >
                    {/* Date gridlines */}
                    {occ.dates.map((d, i) => {
                      const isToday = d === todayISO;
                      return (
                        <div
                          key={d}
                          className={`absolute top-0 bottom-0 border-r border-slate-100 ${isToday ? "bg-blue-50/40" : ""}`}
                          style={{ left: i * CELL_W, width: CELL_W }}
                        />
                      );
                    })}
                    {retreatTracks.map((track, tIdx) =>
                      track.map(r => {
                        const bar = computeRetreatBar(r.start_date, r.end_date);
                        if (!bar) return null;
                        const color = RETREAT_BAR_COLORS[r.status] || RETREAT_BAR_COLORS.planned;
                        const topPx = TRACK_GAP + tIdx * (TRACK_H + TRACK_GAP);
                        const partner = r.partner?.name || "";
                        return (
                          <Link
                            to={`/retreats/${r.id}`}
                            key={r.id}
                            data-testid={`retreat-bar-${r.id}`}
                            title={`${r.name}${partner ? ` · ${partner}` : ""} · ${r.start_date} → ${r.end_date} · ${RETREAT_STATUS_LABEL[r.status] || r.status}`}
                            style={{
                              left: bar.leftPx,
                              width: Math.max(12, bar.widthPx - 2),
                              top: topPx,
                              height: TRACK_H,
                              borderTopLeftRadius: bar.clipLeft ? 0 : undefined,
                              borderBottomLeftRadius: bar.clipLeft ? 0 : undefined,
                              borderTopRightRadius: bar.clipRight ? 0 : undefined,
                              borderBottomRightRadius: bar.clipRight ? 0 : undefined,
                            }}
                            className={`absolute ${color} text-xs font-semibold pl-3 pr-3 rounded-md shadow-sm transition-colors border-l-[3px] focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1 overflow-hidden whitespace-nowrap text-ellipsis flex items-center cursor-pointer`}
                          >
                            <span className="truncate">
                              {r.name}
                              {partner && <span className="opacity-70 font-normal"> · {partner}</span>}
                            </span>
                          </Link>
                        );
                      }),
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Body: one row per unit; bars are absolutely positioned */}
            {Object.entries(groupedUnits).map(([group, units]) => (
              <Fragment key={group}>
                <div className="bg-slate-100/70 px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-600 border-b border-slate-200">
                  {group}
                </div>
                {units.map(u => {
                  const tracks = tracksByUnit[u.id] || [];
                  const rowHeight = Math.max(1, tracks.length) * (TRACK_H + TRACK_GAP) + TRACK_GAP;
                  return (
                    <div key={u.id} data-unit-id={u.id} className="flex border-b border-slate-100 hover:bg-slate-50/30 relative group">
                      <div className="w-[144px] shrink-0 px-3 py-2 font-medium text-sm text-slate-800 bg-white border-r border-slate-200 sticky left-0 z-[5] flex items-center">
                        {u.name}
                      </div>
                      <div
                        className="relative flex-1"
                        style={{ height: rowHeight, width: occ.dates.length * CELL_W }}
                      >
                        {/* Date gridlines */}
                        {occ.dates.map((d, i) => {
                          const isToday = d === todayISO;
                          return (
                            <div
                              key={d}
                              className={`absolute top-0 bottom-0 border-r border-slate-100 ${isToday ? "bg-blue-50/40" : ""}`}
                              style={{ left: i * CELL_W, width: CELL_W }}
                            />
                          );
                        })}

                        {/* Reservation bars */}
                        {tracks.map((track, trackIdx) => (
                          track.map((r) => {
                            // If this bar is being dragged to another unit, hide it from the source row.
                            if (drag && drag.r.id === r.id && drag.previewUnitId && drag.previewUnitId !== u.id) {
                              return null;
                            }
                            // If this reservation is being dragged, use preview dates
                            const effectiveR = (drag && drag.r.id === r.id)
                              ? { ...r, check_in: drag.previewCheckIn, check_out: drag.previewCheckOut }
                              : r;
                            const bar = computeBar(effectiveR);
                            if (!bar) return null;
                            const color = BAR_COLORS[r.payment_status] || BAR_COLORS.unpaid;
                            const topPx = TRACK_GAP + trackIdx * (TRACK_H + TRACK_GAP);
                            const isDragging = drag && drag.r.id === r.id && drag.moved;
                            return (
                              <div
                                key={r.id}
                                style={{
                                  left: bar.leftPx,
                                  width: Math.max(12, bar.widthPx - 2),
                                  top: topPx,
                                  height: TRACK_H,
                                }}
                                className="absolute"
                              >
                                <button
                                  onMouseDown={(e) => {
                                    // Only start 'move' drag on primary click and not on edge handles
                                    if (e.button !== 0) return;
                                    setDrag({
                                      r, edge: "move",
                                      startX: e.clientX,
                                      daysDelta: 0,
                                      previewCheckIn: r.check_in,
                                      previewCheckOut: r.check_out,
                                      previewUnitId: r.unit_id,
                                      moved: false,
                                    });
                                  }}
                                  onClick={(e) => {
                                    if (justDraggedRef.current) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      return;
                                    }
                                    openDrawer(r.id);
                                  }}
                                  title={`${r.guest_name}${(r.extra_guest_names || []).length ? ` / ${r.extra_guest_names.join(" / ")}` : ""} · ${effectiveR.check_in} → ${effectiveR.check_out} · ${r.payment_status}`}
                                  data-testid={`occ-bar-${r.id}`}
                                  className={`w-full h-full ${color} text-white text-xs font-semibold pl-3 pr-3 rounded-md shadow-sm transition-colors border-l-[3px] focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1 overflow-hidden whitespace-nowrap text-ellipsis flex items-center cursor-grab active:cursor-grabbing ${isDragging ? "ring-2 ring-slate-900 ring-offset-1 opacity-90" : ""}`}
                                  style={{
                                    borderTopLeftRadius: bar.clipLeft ? 0 : undefined,
                                    borderBottomLeftRadius: bar.clipLeft ? 0 : undefined,
                                    borderTopRightRadius: bar.clipRight ? 0 : undefined,
                                    borderBottomRightRadius: bar.clipRight ? 0 : undefined,
                                  }}
                                >
                                  {(() => {
                                    const names = [r.guest_name, ...(r.extra_guest_names || []).filter(Boolean)];
                                    // On the bar, show up to 2 names "name / name".
                                    // Any further guests collapse into "+N".
                                    if (names.length <= 2) return names.join(" / ");
                                    return `${names[0]} / ${names[1]} +${names.length - 2}`;
                                  })()}
                                  {isDragging && (
                                    <span className="ml-1 opacity-80 truncate">
                                      {drag.edge === "right" && `→ ${drag.previewCheckOut}`}
                                      {drag.edge === "left" && `← ${drag.previewCheckIn}`}
                                      {drag.edge === "move" && `↔ ${drag.previewCheckIn} → ${drag.previewCheckOut}`}
                                    </span>
                                  )}
                                </button>
                                {/* Left-edge drag handle — change check-in */}
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setDrag({
                                      r, edge: "left",
                                      startX: e.clientX,
                                      daysDelta: 0,
                                      previewCheckIn: r.check_in,
                                      previewCheckOut: r.check_out,
                                      moved: false,
                                    });
                                  }}
                                  className="absolute top-0 left-0 h-full w-2 cursor-ew-resize hover:bg-white/40 rounded-l-md"
                                  data-testid={`occ-resize-left-${r.id}`}
                                  title="Drag to change check-in date"
                                />
                                {/* Right-edge drag handle — change check-out */}
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setDrag({
                                      r, edge: "right",
                                      startX: e.clientX,
                                      daysDelta: 0,
                                      previewCheckIn: r.check_in,
                                      previewCheckOut: r.check_out,
                                      moved: false,
                                    });
                                  }}
                                  className="absolute top-0 right-0 h-full w-2 cursor-ew-resize hover:bg-white/40 rounded-r-md"
                                  data-testid={`occ-resize-${r.id}`}
                                  title="Drag to change check-out date"
                                />
                              </div>
                            );
                          })
                        ))}

                        {/* Ghost bar in the target row while dragging to reassign room */}
                        {drag && drag.edge === "move" && drag.moved && drag.previewUnitId === u.id && drag.r.unit_id !== u.id && (() => {
                          const effectiveR = { ...drag.r, check_in: drag.previewCheckIn, check_out: drag.previewCheckOut };
                          const bar = computeBar(effectiveR);
                          if (!bar) return null;
                          return (
                            <div
                              key={`ghost-${drag.r.id}`}
                              style={{
                                left: bar.leftPx,
                                width: Math.max(12, bar.widthPx - 2),
                                top: TRACK_GAP,
                                height: TRACK_H,
                              }}
                              className="absolute rounded-md border-2 border-dashed border-slate-900/60 bg-slate-900/10 flex items-center px-3 text-xs font-semibold text-slate-800 pointer-events-none whitespace-nowrap overflow-hidden"
                              data-testid={`occ-ghost-${drag.r.id}`}
                            >
                              {drag.r.guest_name} → move here
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            ))}
            {occ.units.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">No units seeded yet</div>
            )}
          </div>
        </div>
      </Card>

      <ReservationDrawer
        reservationId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onChanged={load}
      />
      <AddReservationDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
    </div>
  );
}
